import { Children, ReactNode } from 'react'
import { Button } from '@firstbite/ui'

/**
 * Layout container for a wizard step's footer. Lays out a single child
 * full-width, or two children (Back | primary) as an even two-up row. Compose
 * with `WizardFooter.Back` / `WizardFooter.Next`:
 *
 * ```tsx
 * <WizardFooter.Root>
 *   <WizardFooter.Back onClick={goBack} />
 *   <WizardFooter.Next onClick={onSubmit} disabled={!isValid}>
 *     Create
 *   </WizardFooter.Next>
 * </WizardFooter.Root>
 * ```
 */
function Root({ children }: { children: ReactNode }) {
  const items = Children.toArray(children)
  if (items.length > 1) {
    return (
      <div className="flex gap-4 p-2 justify-between">
        {items.map((child, i) => (
          <div key={i} className="w-1/2">
            {child}
          </div>
        ))}
      </div>
    )
  }
  return <div className="p-2">{children}</div>
}

/**
 * Outline "Back"/step-back button for a wizard footer.
 *
 * @param onClick - Step-back / exit handler.
 * @param label - Button label (defaults to "Back").
 * @param disabled - Disable while a mutation is in flight, etc.
 */
function Back({
  onClick,
  label = 'Back',
  disabled,
}: {
  onClick: () => void
  label?: string
  disabled?: boolean
}) {
  return (
    <Button
      className="w-full"
      type="button"
      size="lg"
      variant="primary"
      appearance="outline"
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </Button>
  )
}

/**
 * Primary forward/submit button for a wizard footer. The label is the button's
 * children so it can be dynamic (e.g. "Create" ↔ "Creating…").
 *
 * @param onClick - Forward / submit handler.
 * @param disabled - Disable when the step is incomplete or submitting.
 */
function Next({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <Button
      className="w-full"
      type="button"
      size="lg"
      variant="primary"
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </Button>
  )
}

Root.displayName = 'WizardFooter.Root'
Back.displayName = 'WizardFooter.Back'
Next.displayName = 'WizardFooter.Next'

/**
 * Compound footer for the full-screen mobile wizards. `Root` is the layout
 * container; `Back` and `Next` are the button pieces. Because each button owns
 * its own `onClick` / `disabled` / label, footers compose freely — Next-only,
 * Back-only, or Back + Next — with no per-combination component or qualified
 * prop names. Pass a `<WizardFooter.Root>` as `MobileModal`'s `footer`.
 */
export const WizardFooter = { Root, Back, Next }
